import * as React from "react";
import * as cn from "classnames/bind";
import { BurdeFontResponse, TextInformation } from "burde";
import * as styles from "./cover-text-input.css";
import Select from "react-select";
import { ValidFontSizes } from "svg/constants";
import { Option } from "appz";
import ZentaColorPicker, {
    ZentaColorPickerItem,
} from "layout/common/controls-area/zenta-color-picker";
import Slider from "rc-slider";

export type FontItem = {
    Name: string;
    Value: string;
};

const cx = cn.bind(styles);

const blockCss = () =>
    cx({
        textBlock: true,
    });

const rowCss = () =>
    cx({
        row: true,
    });

const inputCss = () =>
    cx({
        inputField: true,
    });

const col33 = () =>
    cx({
        col33: true,
    });

const col66 = () =>
    cx({
        col66: true,
    });

const removeTextButtonCss = () =>
    cx({
        removeTextButton: true,
    });

const removeTextButtonXCss = () =>
    cx({
        fas: true,
        "fa-times": true,
    });

const rotateTextButton = (rotateTextButtonActive: boolean) =>
    cx({
        rotateTextButton: true,
        rotateTextButtonActive,
    });

const getFontSizeOptions = (
    fontSizes: Array<FontItem>,
    textItem: TextInformation,
): Array<Option<string>> => {
    const options = fontSizes.map((x) => ({ label: x.Name, value: x.Value }));

    const selectedFontSizeOption = options.find(
        (x) => x.value === textItem.fontSize,
    );

    if (!selectedFontSizeOption) {
        options.push({ label: textItem.fontSize, value: textItem.fontSize });
    }

    return options;
};

const fontStyles = {
    option: (styles: any, data: any) => {
        return {
            ...styles,
            fontFamily: data.value,
        };
    },
    singleValue: (styles: any, data: any) => {
        return {
            ...styles,
            fontFamily: data.data.value,
        };
    },
};

interface CoverTextInputItemProps {
    placeholderText: string;
    textItem: TextInformation;
    textColors: Array<ZentaColorPickerItem>;
    index: number;
    removeTextItem: (item: TextInformation) => void;
    updateText: (value: string, index: number) => void;
    updateFontSize: (value: string, index: number) => void;
    updateFont: (value: number, index: number) => void;
    updateTextColor: (value: any, index: number) => void;
    updateRotation: (value: number, index: number) => void;
    updateCustomTextColor?: (value: any, index: number) => void;
    fontOptions: BurdeFontResponse | null;
    enableTextRotationLabel: string;
}

const CoverTextInputItem: React.FC<CoverTextInputItemProps> = ({
    placeholderText,
    textItem,
    textColors,
    index,
    fontOptions,
    removeTextItem,
    updateText,
    updateFont,
    updateFontSize,
    updateTextColor,
    updateRotation,
    updateCustomTextColor = () => {},
    enableTextRotationLabel,
}) => {
    const [rotationEnabled, setRotationEnabled] = React.useState(
        textItem.rotation !== 0,
    );

    React.useEffect(() => {
        if (textItem.rotation !== 0) {
            setRotationEnabled(true);
        }
    }, [textItem.rotation]);

    const handleRotationEnabledChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const checked = e.target.checked;
        setRotationEnabled(checked);
        if (!checked) {
            updateRotation(0, index);
        }
    };

    const marks = {
        "-90": (
            <button className={rotateTextButton(textItem.rotation === -90)}>
                -90°
            </button>
        ),
        "-45": (
            <button className={rotateTextButton(textItem.rotation === -45)}>
                -45°
            </button>
        ),
        0: (
            <button className={rotateTextButton(textItem.rotation === 0)}>
                0°
            </button>
        ),
        45: (
            <button className={rotateTextButton(textItem.rotation === 45)}>
                45°
            </button>
        ),
        90: (
            <button className={rotateTextButton(textItem.rotation === 90)}>
                90°
            </button>
        ),
    };

    const getFontOptions = () => {
        if (!fontOptions) {
            return [];
        }

        let options = fontOptions.fontResources.map((k) => ({
            label: k.title,
            value: k.id,
        }));

        return options;
    };

    const getSelectedFontById = (fontId: number | null) => {
        // Legacy fonts
        if (textItem.fontSetName) {
            return {
                label: textItem.fontSetName,
                value: 0,
            };
        }

        let label = "";

        if (fontOptions) {
            fontOptions.fontResources.forEach((option) => {
                if (option.id === fontId) {
                    label = option.title;
                }
            });
        }

        return {
            label,
            value: fontId ? fontId : 0,
        };
    };

    const getSelectedColorItem = () => {
        if (!!textItem.color) {
            const selectedItem = textColors.find(
                (x) =>
                    x.hexColor.toLowerCase() === textItem.color.toLowerCase(),
            );
            return selectedItem ? selectedItem.id : -1;
        }
        return null;
    };

    const fontSizeOptions = getFontSizeOptions(ValidFontSizes, textItem);
    const selectedFontSizeOption = fontSizeOptions.find(
        (x) => (x as any).value === textItem.fontSize,
    );

    return (
        <div className={blockCss()}>
            <button
                type="button"
                className={removeTextButtonCss()}
                onClick={() => removeTextItem(textItem)}>
                <i className={removeTextButtonXCss()} />
            </button>

            <input
                className={inputCss()}
                type="text"
                value={textItem.text}
                placeholder={placeholderText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    updateText(e.target.value, index);
                }}
            />

            <div className={rowCss()}>
                <div className={col33()}>
                    {" "}
                    <Select
                        isSearchable={false}
                        options={fontSizeOptions}
                        value={selectedFontSizeOption}
                        onChange={(selectedValue) => {
                            updateFontSize((selectedValue as any).value, index);
                        }}
                    />
                </div>

                <div className={col66()}>
                    {" "}
                    <Select
                        isSearchable={false}
                        options={getFontOptions()}
                        value={getSelectedFontById(textItem.fontId)}
                        styles={fontStyles}
                        onChange={(selectedValue) =>
                            updateFont((selectedValue as any).value, index)
                        }
                    />
                </div>
            </div>

            <div style={{ position: "relative", marginBottom: "40px" }}>
                <ZentaColorPicker
                    selectedItemId={getSelectedColorItem()}
                    colors={textColors}
                    onClick={(colorId) => updateTextColor(colorId, index)}
                    colorsWithBorder={[1]}
                    useColorPicker={true}
                    selectedColor={textItem.color}
                    setCustomColor={(color: string) => {
                        updateCustomTextColor(color, index);
                    }}
                />
            </div>
            <div className={rowCss()} style={{ padding: "0 25px 20px" }}>
                <div style={{ margin: "0 auto 20px" }}>
                    <label style={{ cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={rotationEnabled}
                            onChange={handleRotationEnabledChange}
                            style={{ marginRight: 5 }}
                        />
                        {enableTextRotationLabel}
                    </label>
                </div>
                <Slider
                    disabled={!rotationEnabled}
                    min={-90}
                    max={90}
                    marks={marks}
                    value={textItem.rotation}
                    defaultValue={0}
                    onChange={(value: any) => {
                        updateRotation(value, index);
                    }}
                    included={false}
                    trackStyle={{ backgroundColor: "#cfebef" }}
                    handleStyle={{
                        borderColor: "#cfebef",
                        borderWidth: "2px",
                        height: 20,
                        width: 20,
                        marginTop: -8,
                    }}
                    railStyle={{ backgroundColor: "#cfebef" }}
                    dotStyle={{
                        borderColor: "#cfebef",
                    }}
                />
            </div>
        </div>
    );
};

export default CoverTextInputItem;
